import { DefaultVolume, isVolumeStep } from '../audio/volume';
import type { Language } from '../i18n/messages';
import { isRecord } from '../guards';
import { COUNTRY_CODES } from '../hall-of-fame/country-codes';
import {
  DEFAULT_HIGH_SCORES,
  parseHighScores,
  type HighScoreEntry,
} from '../hall-of-fame/high-scores';
import { DEFAULT_BINDINGS } from '../input/default-bindings';
import { parseBindings } from '../input/parse-bindings';
import { NO_COUNTRY } from '../options';
import type { EffectSettings, Options } from '../options';
import type { StoragePort } from '../ports/storage-port';

/** The one key of the save document (ADR-0006 decision 2, GDD §9.8). */
export const SAVE_KEY = 'retro-shmup.v1';

/** Version 2 adds the bindings (GDD v0.15 §4.2); version 1 migrates by adding the defaults. */
const VERSION = 2;
const FIRST_VERSION = 1;

const LANGUAGES: readonly Language[] = ['en', 'fr'];

/**
 * The options of a first launch (GDD §9.3): every effect on, except that reduced motion presets
 * the shake, the flashes and the slow motion off. The blink stays on until its non-flashing form
 * exists (interim, meta/05-state-scenes).
 */
export function defaultOptions(reducedMotion: boolean): Options {
  return {
    effects: {
      blink: true,
      hitFlash: !reducedMotion,
      explosions: true,
      shake: !reducedMotion,
      bombFlash: !reducedMotion,
      slowMotion: !reducedMotion,
      championName: true,
      graze: true,
      chainHints: true,
    },
    autofire: false,
    language: 'en',
    fullscreen: false,
    bindings: DEFAULT_BINDINGS,
    musicVolume: DefaultVolume.music,
    sfxVolume: DefaultVolume.sfx,
  };
}

/**
 * A stored volume step, by allowlist: the volumes came after save version 2, so a missing one
 * reads as its default (GDD v0.23 §9.8); anything but an integer from 0 to 10 is invalid.
 */
function parseVolume(value: unknown, fallback: number): number | undefined {
  if (value === undefined) return fallback;
  return isVolumeStep(value) ? value : undefined;
}

/**
 * The stored country choice, by allowlist (GDD v0.26 §9.8): `none` or a code of `COUNTRY_CODES`;
 * anything else, absence included, reads as never asked, so the question comes again.
 */
function parseCountry(value: unknown): string | undefined {
  if (value === NO_COUNTRY) return NO_COUNTRY;
  return typeof value === 'string' && COUNTRY_CODES.has(value) ? value : undefined;
}

/** The stored feedback count (GDD v0.26 §9.8): a whole number from 0, anything else absent. */
function parseCount(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

/**
 * Rebuilds the effects from the known keys only; `undefined` if any is not a boolean. The
 * champion's name came after save version 2, so a missing key reads as on (GDD v0.22 §9.4.1).
 */
function parseEffects(value: unknown): EffectSettings | undefined {
  if (!isRecord(value)) return undefined;
  const { blink, hitFlash, explosions, shake, bombFlash, slowMotion } = value;
  const championName = value.championName === undefined ? true : value.championName;
  // Added in GDD v0.31 to a version-2 save: an absent key is the default, on (#146).
  const graze = value.graze === undefined ? true : value.graze;
  // Added in GDD v0.32 the same way (#147).
  const chainHints = value.chainHints === undefined ? true : value.chainHints;
  if (
    typeof blink !== 'boolean' ||
    typeof hitFlash !== 'boolean' ||
    typeof explosions !== 'boolean' ||
    typeof shake !== 'boolean' ||
    typeof bombFlash !== 'boolean' ||
    typeof slowMotion !== 'boolean' ||
    typeof championName !== 'boolean' ||
    typeof graze !== 'boolean' ||
    typeof chainHints !== 'boolean'
  ) {
    return undefined;
  }
  return {
    blink,
    hitFlash,
    explosions,
    shake,
    bombFlash,
    slowMotion,
    championName,
    graze,
    chainHints,
  };
}

/**
 * Allowlist parsing (ADR-0006, INPUT-1): a new object of the known fields, or `undefined`. A
 * version 1 document has no bindings: it migrates with the defaults (ADR-0006 decision 4).
 */
function parseOptions(value: unknown, version: number): Options | undefined {
  if (!isRecord(value)) return undefined;
  const effects = parseEffects(value.effects);
  const { autofire, fullscreen, language } = value;
  if (effects === undefined) return undefined;
  if (typeof autofire !== 'boolean' || typeof fullscreen !== 'boolean') return undefined;
  const known = LANGUAGES.find((candidate) => candidate === language);
  if (known === undefined) return undefined;
  const musicVolume = parseVolume(value.musicVolume, DefaultVolume.music);
  const sfxVolume = parseVolume(value.sfxVolume, DefaultVolume.sfx);
  if (musicVolume === undefined || sfxVolume === undefined) return undefined;
  const bindings = version === FIRST_VERSION ? DEFAULT_BINDINGS : parseBindings(value.bindings);
  const options = {
    effects,
    autofire,
    language: known,
    fullscreen,
    bindings,
    musicVolume,
    sfxVolume,
  };
  const country = parseCountry(value.country);
  const feedbackCount = parseCount(value.feedbackCount);
  return {
    ...options,
    ...(country === undefined ? {} : { country }),
    ...(feedbackCount === undefined ? {} : { feedbackCount }),
  };
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined; // An unreadable document resolves to the defaults (ADR-0006 decision 4).
  }
}

/**
 * The save document's schema rules (ADR-0006, meta/04-class-menus): `StoragePort` moves strings,
 * this reads and writes the versioned document. Untrusted input: parsing never throws, and an
 * unreadable, invalid or unknown document resolves to the defaults. Reads and writes happen at
 * discrete moments only, never inside the fixed step.
 */
export class SaveStore {
  private readonly storage: StoragePort;
  private readonly reducedMotion: boolean;
  /** A newer document read by an older build is never overwritten this session (UC4 2c). */
  private readOnly = false;
  /** The stored top 10, read by allowlist (ADR-0021), or the default table; saving writes it back. */
  private table: readonly HighScoreEntry[] = DEFAULT_HIGH_SCORES;
  /** False until `load`: nothing is written before the stored document has been read. */
  private loaded = false;
  /** The options last loaded or saved, written with the table. */
  private options: Options;

  constructor(storage: StoragePort, reducedMotion: boolean) {
    this.storage = storage;
    this.reducedMotion = reducedMotion;
    this.options = defaultOptions(reducedMotion);
  }

  /**
   * The stored options, or the defaults of a first launch; also fills `highScores`: the stored
   * table, or the default one when the save holds none (GDD v0.28 §9.1).
   */
  load(): Options {
    this.loaded = true;
    const fallback = defaultOptions(this.reducedMotion);
    this.options = fallback;
    this.table = DEFAULT_HIGH_SCORES;
    const raw = this.storage.read(SAVE_KEY);
    if (raw === null) return fallback;
    const document = parseJson(raw);
    if (!isRecord(document)) return fallback;
    const version = document.version;
    if (typeof version === 'number' && version > VERSION) {
      this.readOnly = true;
      return fallback;
    }
    if (version !== VERSION && version !== FIRST_VERSION) return fallback;
    // Only an absent key takes the fictional players; a stored table, empty included, is kept.
    if (document.highScores !== undefined) this.table = parseHighScores(document.highScores);
    this.options = parseOptions(document.options, version) ?? fallback;
    return this.options;
  }

  /**
   * Writes the whole document once; `false` when storage is unavailable or the stored document
   * is newer: the session goes on without saving. The current top 10 is written with them.
   */
  saveOptions(options: Options): boolean {
    this.options = options;
    return this.write();
  }

  /** The top 10 read by `load`, best first. */
  get highScores(): readonly HighScoreEntry[] {
    return this.table;
  }

  /** Writes the new top 10 with the options last saved or loaded (GDD v0.16 §9.1). */
  saveHighScores(entries: readonly HighScoreEntry[]): boolean {
    this.table = entries;
    return this.write();
  }

  private write(): boolean {
    if (this.readOnly || !this.loaded) return false;
    const document = { version: VERSION, options: this.options, highScores: this.table };
    return this.storage.write(SAVE_KEY, JSON.stringify(document));
  }
}
